import React from "react";
import ErrorMess from "../ui/ErrorMess";
import { MonthPicker } from "../ui/MonthPicker";
import { getEarliestValidStartDate } from "../../utils/dateUtils";
import { Partner, PartnerContact } from "../../types/addServiceTypes";
import type { OfftakeFormData } from "../../pages/AddService";

// S5c — PURE REFACTOR (plan §5g/§5h). The step-3 "Offtake Contract Parameters" form,
// lifted VERBATIM out of AddService.tsx (origin/main:3033-3625). Presentational only:
// it owns no state and performs no side effects — every value and callback is injected.
// Props deliberately reuse the ORIGINAL identifier names so the JSX body is
// byte-for-byte identical to the block it replaces, which is what makes this refactor
// provably inert.
//
// Needed for the MIRROR case (§5j): when Company A holds injection only, the second
// leg is OFFTAKE. Carries the two fields with no injection equivalent — the required
// `Product *` select (FlexiGreen / LocalGreen -> offtakeFormData.product) and the
// admin-only Price Index — per §5j asymmetry 1.
//
// 🔴 KNOWN DEBT, deliberately preserved: this form contains ZERO t() calls — every
// string is a hardcoded English literal, unlike the injection form's 48 t() calls.
// Translating it is a user-visible behaviour change for FR/NL and therefore must NOT
// ride inside this refactor; it is logged as a separate follow-up. The strings below
// are reproduced exactly as they were.

// Only the pslData fields this form reads. DataPSL["ean"] is structurally assignable.
interface OfftakeContractParamsPsl {
  gsrn: string;
  apStreetName: string;
  apApartmentNumber: string;
  apZip: string;
  apCity: string;
  country: string;
  runningProcessLabel: string;
}

// The partner-selection cluster, grouped so the prop list stays readable. Destructured
// back to its original names below so the JSX body needs no edits.
export interface OfftakePartnerProps {
  partners: Partner[];
  partnerContacts: PartnerContact[];
  selectedPartnerLocal: Partner | null;
  partnerSearchTerm: string;
  setPartnerSearchTerm: (value: string) => void;
  showPartnerDropdown: boolean;
  setShowPartnerDropdown: (value: boolean) => void;
  handlePartnerChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handlePartnerContactChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  lastSelectedOfftakePartnerId: number | null;
  setLastSelectedOfftakePartnerId: (id: number | null) => void;
}

interface OfftakeContractParametersProps {
  offtakeFormData: OfftakeFormData;
  handleOfftakeFormChange: <K extends keyof OfftakeFormData>(field: K, value: OfftakeFormData[K]) => void;
  pslData: OfftakeContractParamsPsl | null;
  selectedUserCompany?: { companyName?: string } | null;
  getMinEndDate: (start: string) => string;
  showVolumeConfirmation: boolean;
  isAdminOrPartner: boolean;
  user?: { user: { role: string } | null } | null;
  formValidationError: string | null;
  injectionError: string | null;
  offtakeError: string | null;
  partner: OfftakePartnerProps;
  // Modelled on bessOnboarding/ContractProp.tsx's StepNavProps.
  handleFormSubmit: (e?: React.FormEvent) => void;
  // Onboarding site create/link block, rendered beneath the read-only address card.
  siteSection?: React.ReactNode;
}

export const OfftakeContractParameters: React.FC<OfftakeContractParametersProps> = ({
  offtakeFormData,
  handleOfftakeFormChange,
  pslData,
  selectedUserCompany,
  getMinEndDate,
  showVolumeConfirmation,
  isAdminOrPartner,
  user,
  formValidationError,
  injectionError,
  offtakeError,
  partner,
  handleFormSubmit,
  siteSection,
}) => {
  const {
    partners,
    partnerContacts,
    selectedPartnerLocal,
    partnerSearchTerm,
    setPartnerSearchTerm,
    showPartnerDropdown,
    setShowPartnerDropdown,
    handlePartnerChange,
    handlePartnerContactChange,
    lastSelectedOfftakePartnerId,
    setLastSelectedOfftakePartnerId,
  } = partner;

  return (
                <form className="max-w-4xl mx-auto">
                  <h2 className="text-primary-600 mb-6 font-semibold text-2xl">Offtake Contract Parameters</h2>

                  {/* EAN Company Name and Address */}
                  {pslData && (
                    <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
                      <div className="font-semibold text-primary-600 text-base mb-1">
                        {selectedUserCompany?.companyName}
                      </div>
                      <div className="text-primary-500 text-sm">
                        {pslData.apStreetName}
                        {pslData.apApartmentNumber && ` ${pslData.apApartmentNumber}`}, {pslData.apZip} {pslData.apCity}
                        , {pslData.country}
                      </div>
                      <div className="text-primary-400 text-xs mt-1">EAN: {pslData.gsrn}</div>
                    </div>
                  )}

                  {siteSection}

                  <div className="flex gap-6">
                    {/* Left Column */}
                    <div className="flex flex-1 flex-col gap-4">
                      {/* Contract Dates */}
                      <div className="flex gap-4">
                        <div className="flex flex-1 flex-col">
                          <label htmlFor="startDate" className="text-primary-600 mb-2 font-medium text-sm">
                            Contract Start Date *
                          </label>
                          <MonthPicker
                            id="startDate"
                            value={offtakeFormData.startDate}
                            onChange={(val) => handleOfftakeFormChange("startDate", val)}
                            minDate={getEarliestValidStartDate()}
                            placeholder="Select start month"
                            disabled={pslData?.runningProcessLabel?.toLowerCase().includes("under construction")}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {pslData?.runningProcessLabel?.toLowerCase().includes("under construction") ? (
                              <>
                                <span className="text-yellow-600 font-semibold">This meter is under construction.</span>
                                <br />
                                The contract start date will be determined once construction is complete.
                                <br />
                                Please contact Sales for an estimated activation date.
                              </>
                            ) : (
                              <>
                                Contracts start on the 1st day of a month. Earliest start: the 1st of next month (or
                                today on the 1st).
                                <br />
                                For earlier start, please contact Sales directly.
                              </>
                            )}
                          </p>
                        </div>

                        <div className="flex flex-1 flex-col">
                          <label htmlFor="endDate" className="text-primary-600 mb-2 font-medium text-sm">
                            Contract End Date
                          </label>

                          {/* Radio button options */}
                          <div className="space-y-2 mb-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                id="flexible-offtake"
                                name="contract-end-offtake"
                                checked={offtakeFormData.isFlexible}
                                onChange={() => handleOfftakeFormChange("isFlexible", true)}
                                className="accent-primary-600"
                              />
                              <label htmlFor="flexible-offtake" className="text-primary-600 text-sm">
                                Automatic monthly renewal
                              </label>
                            </div>

                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                id="specific-end-offtake"
                                name="contract-end-offtake"
                                checked={!offtakeFormData.isFlexible}
                                onChange={() => handleOfftakeFormChange("isFlexible", false)}
                                className="accent-primary-600"
                              />
                              <label htmlFor="specific-end-offtake" className="text-primary-600 text-sm">
                                Specific end date
                              </label>
                            </div>
                          </div>

                          {/* Show date input only when specific end date is selected */}
                          {!offtakeFormData.isFlexible && (
                            <input
                              type="date"
                              id="endDate"
                              value={offtakeFormData.endDate}
                              onChange={(e) => handleOfftakeFormChange("endDate", e.target.value)}
                              min={getMinEndDate(offtakeFormData.startDate)}
                              className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20 mb-1"
                            />
                          )}

                          <p className="text-xs text-gray-500">
                            {offtakeFormData.isFlexible
                              ? "You can terminate this contract at the end of any month."
                              : "Must be at least 1 month after start date"}
                          </p>
                        </div>
                      </div>

                      {/* Annual Offtake Volume */}
                      <div className="flex flex-col">
                        <label htmlFor="annualOfftake" className="text-primary-600 mb-2 font-medium text-sm">
                          Annual Offtake Volume (MWh) *
                        </label>

                        {/* Show input by default or when checkbox is unchecked */}
                        {!offtakeFormData.annualVolumeUnknown ? (
                          <>
                            <input
                              type="number"
                              id="annualOfftake"
                              placeholder="e.g. 10000"
                              min="1"
                              value={offtakeFormData.annualVolume || ""}
                              onChange={(e) => handleOfftakeFormChange("annualVolume", Number(e.target.value))}
                              className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20"
                              required={!offtakeFormData.annualVolumeUnknown}
                            />
                            {showVolumeConfirmation && (
                              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mt-2">
                                <div className="flex">
                                  <div className="flex-shrink-0">
                                    <svg
                                      className="h-5 w-5 text-yellow-400"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm text-yellow-700">
                                      Are you sure you consume over 1000 MWh per year? This is a large volume and may
                                      require special handling.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          /* Show info message when unknown is checked */
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                            <p className="text-sm text-gray-600">
                              The annual volume will be estimated based on historical data or meter configuration.
                            </p>
                          </div>
                        )}

                        {/* Checkbox for unknown volume - now below the input */}
                        <div className="flex items-center gap-2 mt-3">
                          <input
                            type="checkbox"
                            id="unknown-volume-offtake"
                            checked={offtakeFormData.annualVolumeUnknown}
                            onChange={(e) => handleOfftakeFormChange("annualVolumeUnknown", e.target.checked)}
                            className="accent-primary-600"
                          />
                          <label htmlFor="unknown-volume-offtake" className="text-primary-600 text-sm">
                            I don't know the annual volume
                          </label>
                        </div>
                      </div>

                      {/* Product Selection */}
                      <div className="flex flex-col">
                        <label htmlFor="product" className="text-primary-600 mb-2 font-medium text-sm">
                          Product *
                        </label>
                        <select
                          id="product"
                          className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20"
                          value={offtakeFormData.product}
                          onChange={(e) => handleOfftakeFormChange("product", e.target.value)}
                        >
                          <option value="1">FlexiGreen</option>
                          <option value="2">LocalGreen</option>
                        </select>
                        <div className="mt-2 text-xs text-gray-600">
                          <p>
                            <span className="font-medium">FlexiGreen:</span> Green electricity from European origin
                          </p>
                          <p className="mt-1">
                            <span className="font-medium">LocalGreen:</span> Green electricity from Belgian origin
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <label className="text-primary-600 mb-3 font-medium text-sm">
                          Are you working with a Yuso Partner?
                        </label>

                        {/* Radio buttons with card-like design */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <label
                            className={`flex items-center gap-3 py-1 px-3 rounded-lg border-2 cursor-pointer transition-all
      ${offtakeFormData.partnerId === 1 ? "border-primary-100" : "border-gray-100 hover:border-primary-100 bg-white"}
    `}
                          >
                            <input
                              type="radio"
                              name="workingWithPartnerOfftake"
                              checked={offtakeFormData.partnerId === 1}
                              onChange={() => {
                                handleOfftakeFormChange("partnerId", 1); // Set to 1 for Standard Yuso
                                handleOfftakeFormChange("partnerContactId", null);
                              }}
                              className="accent-primary-600 w-4 h-4"
                            />
                            <div>
                              <span
                                className={`font-medium text-sm ${
                                  offtakeFormData.partnerId === 1 ? "text-primary-700" : "text-primary-600"
                                }`}
                              >
                                Standard Yuso
                              </span>
                              <p className="text-xs text-primary-500 mt-0.5">Direct service</p>
                            </div>
                          </label>

                          <label
                            className={`flex items-center gap-3 py-1 px-3 rounded-lg border-2 cursor-pointer transition-all
      ${
        offtakeFormData.partnerId !== null && offtakeFormData.partnerId !== 1
          ? "border-primary-100"
          : "border-gray-100 hover:border-primary-100 bg-white"
      }
    `}
                          >
                            <input
                              type="radio"
                              name="workingWithPartnerOfftake"
                              checked={offtakeFormData.partnerId !== null && offtakeFormData.partnerId !== 1}
                              onChange={() => {
                                if (offtakeFormData.partnerId === null || offtakeFormData.partnerId === 1) {
                                  // Use offtake-specific last selected partner
                                  const partnerIdToSet = lastSelectedOfftakePartnerId || -1;
                                  handleOfftakeFormChange("partnerId", partnerIdToSet);

                                  if (lastSelectedOfftakePartnerId && lastSelectedOfftakePartnerId > 0) {
                                    const partner = partners.find((p) => p.id === lastSelectedOfftakePartnerId);
                                    if (partner) {
                                      setPartnerSearchTerm(partner.name);
                                    }
                                  }
                                }
                              }}
                              className="accent-primary-600 w-4 h-4"
                            />

                            <div>
                              <span
                                className={`font-medium text-sm ${
                                  offtakeFormData.partnerId !== null && offtakeFormData.partnerId !== 1
                                    ? "text-primary-700"
                                    : "text-primary-600"
                                }`}
                              >
                                Partner Service
                              </span>
                              <p className="text-xs text-primary-500 mt-0.5">Via partner</p>
                            </div>
                          </label>
                        </div>

                        {/* Partner selection - animated appearance */}
                        {offtakeFormData.partnerId !== null && offtakeFormData.partnerId !== 1 && (
                          <div className="border border-gray-100 rounded-lg p-4 space-y-4 animate-fadeIn">
                            <div>
                              <label
                                htmlFor="partnerSearchOfftake"
                                className="text-primary-600 mb-2 font-medium text-xs uppercase tracking-wide block"
                              >
                                Select Partner
                              </label>

                              <div className="relative">
                                {/* Search input */}
                                <div className="relative">
                                  <input
                                    id="partnerSearchOfftake"
                                    type="text"
                                    placeholder="Search for a partner..."
                                    value={partnerSearchTerm}
                                    onChange={(e) => setPartnerSearchTerm(e.target.value)}
                                    onFocus={() => setShowPartnerDropdown(true)}
                                    className="w-full border-2 rounded-md px-3 py-2.5 pr-10 border-gray-100 bg-white text-primary-700 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                                  />
                                  <svg
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                    />
                                  </svg>
                                </div>

                                {/* Dropdown with filtered partners */}
                                {showPartnerDropdown && (
                                  <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                                    {partners
                                      .filter((partner) =>
                                        partner.name.toLowerCase().includes(partnerSearchTerm.toLowerCase())
                                      )
                                      .slice(0, 10) // Limit to 10 results for better UX
                                      .map((partner) => (
                                        <button
                                          key={partner.id}
                                          type="button"
                                          onClick={() => {
                                            handleOfftakeFormChange("partnerId", partner.id);
                                            setLastSelectedOfftakePartnerId(partner.id); // Save offtake partner

                                            const syntheticEvent = {
                                              target: { value: partner.id.toString() },
                                            } as React.ChangeEvent<HTMLSelectElement>;
                                            handlePartnerChange(syntheticEvent);
                                            setPartnerSearchTerm(partner.name);
                                            setShowPartnerDropdown(false);
                                          }}
                                          className="w-full text-left px-3 py-2 hover:bg-primary-50 focus:bg-primary-50 focus:outline-none transition-colors text-sm"
                                        >
                                          <div className="font-medium text-primary-700">{partner.name}</div>
                                          {partner.vatNumber && (
                                            <div className="text-xs text-gray-500">VAT: {partner.vatNumber}</div>
                                          )}
                                        </button>
                                      ))}

                                    {/* No results message */}
                                    {partners.filter((partner) =>
                                      partner.name.toLowerCase().includes(partnerSearchTerm.toLowerCase())
                                    ).length === 0 && (
                                      <div className="px-3 py-2 text-sm text-gray-500">
                                        No partners found matching "{partnerSearchTerm}"
                                      </div>
                                    )}

                                    {/* Show more message if there are more results */}
                                    {partners.filter((partner) =>
                                      partner.name.toLowerCase().includes(partnerSearchTerm.toLowerCase())
                                    ).length > 10 && (
                                      <div className="px-3 py-2 text-xs text-gray-500 border-t">
                                        Showing first 10 results. Type more to refine search.
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Selected partner display */}
                              {offtakeFormData.partnerId && offtakeFormData.partnerId > 0 && (
                                <div className="mt-2 p-2 bg-primary-50 rounded-md">
                                  <div className="flex items-center justify-between">
                                    <div className="text-sm">
                                      <span className="text-gray-600">Selected:</span>
                                      <span className="ml-2 font-medium text-primary-700">
                                        {partners.find((p) => p.id === offtakeFormData.partnerId)?.name}
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPartnerSearchTerm("");
                                        handleOfftakeFormChange("partnerId", -1);
                                      }}
                                      className="text-xs text-primary-600 hover:text-primary-700 underline"
                                    >
                                      Change
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Helper text for partner selection */}
                            <div className="flex items-start gap-2 pt-2">
                              <svg
                                className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M18 10a8 8 0 11-16 0 8 8 0 0118 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <p className="text-xs text-primary-600">
                                Your selected partner will manage this service on your behalf
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column (Admin/Partner only) */}
                    {isAdminOrPartner && (
                      <div className="flex flex-1 flex-col gap-4">
                        {/* Platform Fee */}
                        <div className="flex flex-col">
                          <label htmlFor="balancingFee" className="text-primary-600 mb-2 font-medium text-sm">
                            Balancing Fee (€/MWh)
                          </label>
                          <div className="flex">
                            <input
                              type="number"
                              id="balancingFeeOfftake"
                              placeholder="e.g. 8"
                              value={offtakeFormData.balancingFee || ""}
                              onChange={(e) =>
                                handleOfftakeFormChange("balancingFee", e.target.value ? Number(e.target.value) : null)
                              }
                              className="flex-1 border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20 rounded-r-none"
                            />
                            <span className="inline-flex items-center px-3 bg-gray-100 border border-l-0 border-gray-200 rounded-r text-xs text-primary-400">
                              €/MWh
                            </span>
                          </div>
                          {user?.user?.role === "partner" && (
                            <p className="text-xs text-gray-500 mt-1">You can only increase the platform fee</p>
                          )}
                        </div>

                        {/* Partner Information */}
                        <div className="flex flex-col">
                          <label htmlFor="partner" className="text-primary-600 mb-2 font-medium text-sm">
                            Partner
                          </label>
                          <select
                            id="partner"
                            value={offtakeFormData.partnerId || ""}
                            onChange={(e) => {
                              handleOfftakeFormChange("partnerId", e.target.value ? Number(e.target.value) : null);
                              handlePartnerChange(e);
                            }}
                            disabled={user?.user?.role !== "admin"}
                            className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20"
                          >
                            <option value="">Select a partner</option>
                            {partners.map((partner) => (
                              <option key={partner.id} value={partner.id}>
                                {partner.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Partner Contact */}
                        <div className="flex flex-col">
                          <label htmlFor="partnerContact" className="text-primary-600 mb-2 font-medium text-sm">
                            Partner Contact
                          </label>
                          <select
                            id="partnerContactOfftake"
                            value={offtakeFormData.partnerContactId || ""}
                            onChange={(e) => {
                              handleOfftakeFormChange(
                                "partnerContactId",
                                e.target.value ? Number(e.target.value) : null
                              );
                              handlePartnerContactChange(e);
                            }}
                            className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20"
                          >
                            <option value="">Select a contact</option>
                            {partnerContacts.map((contact) => (
                              <option key={contact.id} value={contact.id}>
                                {contact.firstName} {contact.lastName}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Partner Fee */}
                        <div className="flex flex-col">
                          <label htmlFor="partnerFee" className="text-primary-600 mb-2 font-medium text-sm">
                            Partner Fee (€/MWh)
                          </label>
                          <div className="flex">
                            <input
                              type="number"
                              id="partnerFeeOfftake"
                              placeholder="Calculated automatically"
                              value={selectedPartnerLocal?.defaultPartnerFee ?? ""}
                              readOnly
                              className="flex-1 border rounded-md px-3 py-2 border-gray-200 bg-gray-50 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20 rounded-r-none"
                            />
                            <span className="inline-flex items-center px-3 bg-gray-100 border border-l-0 border-gray-200 rounded-r text-xs text-primary-400">
                              €/MWh
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Calculated as: Default Partner Fee + (Balancing Fee - Minimum Platform Fee)/2
                          </p>
                        </div>

                        {/* Price Index (Admin only) */}
                        {user?.user?.role === "admin" && (
                          <div className="flex flex-col">
                            <label htmlFor="priceIndex" className="text-primary-600 mb-2 font-medium text-sm">
                              Price Index
                            </label>
                            <select
                              id="priceIndexOfftake"
                              value={offtakeFormData.priceIndex || ""}
                              onChange={(e) => handleOfftakeFormChange("priceIndex", e.target.value || null)}
                              className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20"
                            >
                              <option value="">Select price index</option>
                              <option value="day_ahead">Day Ahead</option>
                              <option value="imbalance">Imbalance</option>
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mt-6 p-4 bg-gray-50 rounded border border-gray-200">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="confirm-values-combined"
                        checked={offtakeFormData.valuesConfirmed}
                        onChange={(e) => {
                          handleOfftakeFormChange("valuesConfirmed", e.target.checked);
                        }}
                        className="accent-primary-600 w-5 h-5 mt-0.5"
                        required
                      />
                      <label htmlFor="confirm-values-combined" className="text-primary-600 text-sm">
                        <span className="font-medium">I confirm the values provided are correct</span>
                        <p className="text-xs text-gray-500 mt-1">
                          By checking this box, you acknowledge that the information entered above is accurate to the
                          best of your knowledge and will be used to generate your contracts.
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Error Messages */}
                  <div className="mt-4">
                    {formValidationError && <ErrorMess error={formValidationError} />}
                    {injectionError && <ErrorMess error={injectionError} />}
                    {offtakeError && <ErrorMess error={offtakeError} />}
                  </div>

                  {/* Submit Button */}
                  <button
                    onClick={handleFormSubmit}
                    disabled={!offtakeFormData.valuesConfirmed}
                    className={`py-3 px-6 mt-6 rounded-md text-white text-sm font-semibold transition-colors duration-200 ${
                      offtakeFormData.valuesConfirmed
                        ? "bg-primary-600 hover:bg-primary-700"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    Continue to Summary
                  </button>
                </form>
  );
};
